import { Alert, Box, Chip, CircularProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { errorMessage, fetchPeriods } from "@/api/closeReview";

// Close Review: one row per month-end close. fsp-bi-hub runs the checks each
// business day of the close and pushes them here with the P&L, Balance Sheet
// and Statistical Journals; a month that hasn't run yet shows when it will.
export function CloseReviewPage() {
  const navigate = useNavigate();
  const periods = useQuery({ queryKey: ["close-review", "periods"], queryFn: fetchPeriods, retry: false });

  return (
    <AppLayout>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5">Close Review</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "80ch" }}>
            Month-end balance sheet and data-quality checks, run every business day of the close. Open a month to see its
            checks, P&amp;L, Balance Sheet and Statistical Journals, and to comment on or sign off each check.
          </Typography>
        </Box>
        {periods.isLoading && <CircularProgress />}
        {periods.isError && <Alert severity="error">{errorMessage(periods.error)}</Alert>}
        {periods.data && (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Checks</TableCell>
                  <TableCell>Needs review</TableCell>
                  <TableCell>Last run</TableCell>
                  <TableCell>Package</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.data.map((p) => (
                  <TableRow key={p.month} hover tabIndex={0} role="link" sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/close-review/${p.month}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/close-review/${p.month}`); }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography></TableCell>
                    <TableCell>
                      {p.loaded ? (
                        <Stack direction="row" spacing={0.5}>
                          {(["flag", "error", "pass", "info"] as const).map((s) => (p.status_counts[s] ?? 0) > 0 && (
                            <Chip key={s} size="small" label={`${p.status_counts[s]} ${s}`}
                              color={s === "flag" ? "warning" : s === "error" ? "error" : s === "pass" ? "success" : "info"} variant="outlined" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          First run {dayjs(p.schedule.first_checks_at).format("ddd MMM D, h:mm A")} Central
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.loaded && (p.open_flags > 0 || p.changed_since_review > 0) ? (
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                          {`${p.open_flags} to review${p.changed_since_review ? `, ${p.changed_since_review} changed` : ""}`}
                        </Typography>
                      ) : p.loaded ? <Typography variant="body2" color="success.main">All reviewed</Typography> : ""}
                    </TableCell>
                    <TableCell>
                      {p.latest_run_at ? `${dayjs(p.latest_run_at).format("MMM D, h:mm A")}${p.business_day ? ` (business day ${p.business_day})` : ""}` : ""}
                    </TableCell>
                    <TableCell>
                      {p.links.package
                        ? (
                          <Link href={p.links.package} target="_blank" rel="noopener noreferrer" underline="hover"
                            onClick={(e) => e.stopPropagation()}>
                            Financial package
                          </Link>
                        )
                        : <Typography variant="body2" color="text.secondary">Not built yet</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>
    </AppLayout>
  );
}
